import { HttpRequest } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { CsAgentAuthService } from '@spartacus/asm/root'
import {
  AuthRedirectService,
  AuthService,
  AuthStorageService,
  AuthToken,
  GlobalMessageService,
  GlobalMessageType,
  InterceptorUtil,
  OAuthLibWrapperService,
  OccEndpointsService,
  RoutingService,
  USE_CUSTOMER_SUPPORT_AGENT_TOKEN,
  WindowRef
} from '@spartacus/core'
import { CookieService } from 'ngx-cookie-service'
import { take } from 'rxjs/operators'
import { EbLocalStorageKey } from '../../../configuration/models/eb-local-storage.model'
import { EbAuthHttpHeaderService } from '../../auth/services/eb-auth-http-header.service'

@Injectable({
  providedIn: 'root'
})
export class EbAsmAuthHttpHeaderService extends EbAuthHttpHeaderService {
  constructor(
    authService: AuthService,
    protected authStorageService: AuthStorageService,
    oAuthLibWrapperService: OAuthLibWrapperService,
    routingService: RoutingService,
    occEndpoints: OccEndpointsService,
    protected globalMessageService: GlobalMessageService,
    authRedirectService: AuthRedirectService,
    protected cookieService: CookieService,
    protected router: Router,
    protected winRef: WindowRef,
    protected csAgentAuthService: CsAgentAuthService
  ) {
    super(
      authService,
      authStorageService,
      oAuthLibWrapperService,
      routingService,
      occEndpoints,
      globalMessageService,
      authRedirectService,
      cookieService,
      router,
      winRef
    )
  }

  public shouldAddAuthorizationHeader(request: HttpRequest<any>): boolean {
    return super.shouldAddAuthorizationHeader(request) || this.isCSAgentTokenRequest(request)
  }

  /**
   * @override
   *
   * Checks if particular request should be handled by this service.
   */
  public shouldCatchError(request: HttpRequest<any>): boolean {
    return super.shouldCatchError(request) || this.isCSAgentTokenRequest(request)
  }

  /**
   * @override
   *
   * Adds `Authorization` header to occ and CS agent requests.
   * For CS agent requests also removes the `cx-use-csagent-token` header (to avoid problems with CORS).
   */
  public alterRequest(request: HttpRequest<any>, token?: AuthToken): HttpRequest<any> {
    const hasAuthorizationHeader = !!this.getAuthorizationHeader(request)
    const isCSAgentRequest = this.isCSAgentTokenRequest(request)

    let req = super.alterRequest(request)
    if (hasAuthorizationHeader && isCSAgentRequest) {
      return InterceptorUtil.removeHeader(USE_CUSTOMER_SUPPORT_AGENT_TOKEN, req)
    }

    if (!hasAuthorizationHeader && isCSAgentRequest) {
      const asmToken = this.getAsmToken(token)

      if (asmToken?.access_token) {
        req = request.clone({
          setHeaders: {
            Authorization: `Bearer ${asmToken.access_token}`
          }
        })
      } else {
        req = request
      }

      return InterceptorUtil.removeHeader(USE_CUSTOMER_SUPPORT_AGENT_TOKEN, req)
    }
    return req
  }

  private getAsmToken(token?: AuthToken): AuthToken | undefined {
    const tokenAccessToken = token?.access_token?.trim()
    if (tokenAccessToken) {
      return { ...token, access_token: tokenAccessToken }
    }

    try {
      const asmToken = JSON.parse(
        this.winRef.localStorage?.getItem(EbLocalStorageKey.ASM_KEY)
      ) as AuthToken
      const accessToken = asmToken?.access_token?.trim()

      if (accessToken) {
        return { ...asmToken, access_token: accessToken }
      }
    } catch {
      // Fall back to the token managed by Spartacus' auth storage below.
    }

    const authStorageAccessToken = this.authStorageService.getItem('access_token')?.trim()
    return authStorageAccessToken
      ? {
          access_token: authStorageAccessToken,
          access_token_stored_at:
            this.authStorageService.getItem('access_token_stored_at') || new Date().getTime().toString()
        }
      : undefined
  }

  protected isCSAgentTokenRequest(request: HttpRequest<any>): boolean {
    const isRequestWithCSAgentToken = InterceptorUtil.getInterceptorParam(
      USE_CUSTOMER_SUPPORT_AGENT_TOKEN,
      request.headers
    )
    return Boolean(isRequestWithCSAgentToken) || /\/assistedservicewebservices\//.test(request.url)
  }

  /**
   * @override
   *
   * On backend errors indicating expired `refresh_token` we need to logout
   * currently logged in user and CS agent.
   */
  public handleExpiredRefreshToken(): void {
    this.csAgentAuthService
      .isCustomerSupportAgentLoggedIn()
      .pipe(take(1))
      .subscribe(csAgentLoggedIn => {
        if (csAgentLoggedIn) {
          this.authService.setLogoutProgress(true)
          this.csAgentAuthService.logoutCustomerSupportAgent()
          this.globalMessageService.add(
            {
              key: 'asm.csagentTokenExpired'
            },
            GlobalMessageType.MSG_TYPE_ERROR
          )
        } else {
          super.handleExpiredRefreshToken()
        }
      })
  }
}
